import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Users, CheckCircle, XCircle, Plus, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const SelecionarAvaliadores360 = () => {
  const { cicloId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [carregandoFiltros, setCarregandoFiltros] = useState(false);
  const [funcionarios, setFuncionarios] = useState([]);
  const [funcionarioSelecionado, setFuncionarioSelecionado] = useState('');
  const [avaliadoresPares, setAvaliadoresPares] = useState([]);
  const [paresDisponiveis, setParesDisponiveis] = useState([]);
  const [avaliadoresClientes, setAvaliadoresClientes] = useState([]);
  const [clientesDisponiveis, setClientesDisponiveis] = useState([]);
  const [avaliadoresSubordinados, setAvaliadoresSubordinados] = useState([]);
  const [subordinadosDisponiveis, setSubordinadosDisponiveis] = useState([]);

  useEffect(() => {
    carregarFuncionarios();
  }, []);

  useEffect(() => {
    if (funcionarioSelecionado) {
      carregarAvaliadoresPorTipo(funcionarioSelecionado);
    } else {
      setParesDisponiveis([]);
      setClientesDisponiveis([]);
      setSubordinadosDisponiveis([]);
    }
  }, [funcionarioSelecionado]);

  const carregarFuncionarios = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/employees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFuncionarios(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar funcionários:', error);
    }
  };

  const carregarAvaliadoresPorTipo = async (funcId) => {
    setCarregandoFiltros(true);
    try {
      const token = localStorage.getItem('token');
      const [paresRes, clientesRes, subordRes] = await Promise.all([
        axios.get(`${API}/api/employees/elegiveis-avaliacao`, {
          params: { funcionario_id: funcId, tipo: 'pares' },
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/employees/elegiveis-avaliacao`, {
          params: { funcionario_id: funcId, tipo: 'clientes' },
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/employees/elegiveis-avaliacao`, {
          params: { funcionario_id: funcId, tipo: 'subordinados' },
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setParesDisponiveis(paresRes.data || []);
      setClientesDisponiveis(clientesRes.data || []);
      setSubordinadosDisponiveis(subordRes.data || []);
    } catch (error) {
      console.error('Erro ao carregar avaliadores por tipo:', error);
    } finally {
      setCarregandoFiltros(false);
    }
  };

  const toggleAvaliador = (tipo, funcionarioId) => {
    if (tipo === 'pares') {
      if (avaliadoresPares.includes(funcionarioId)) {
        setAvaliadoresPares(avaliadoresPares.filter(id => id !== funcionarioId));
      } else {
        setAvaliadoresPares([...avaliadoresPares, funcionarioId]);
      }
    } else if (tipo === 'clientes') {
      if (avaliadoresClientes.includes(funcionarioId)) {
        setAvaliadoresClientes(avaliadoresClientes.filter(id => id !== funcionarioId));
      } else {
        setAvaliadoresClientes([...avaliadoresClientes, funcionarioId]);
      }
    } else if (tipo === 'subordinados') {
      if (avaliadoresSubordinados.includes(funcionarioId)) {
        setAvaliadoresSubordinados(avaliadoresSubordinados.filter(id => id !== funcionarioId));
      } else {
        setAvaliadoresSubordinados([...avaliadoresSubordinados, funcionarioId]);
      }
    }
  };

  const salvarAvaliadores = async () => {
    if (!funcionarioSelecionado) {
      toast.error('Selecione um funcionário para avaliar');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/api/feedback360/ciclos/${cicloId}/avaliadores`,
        {
          funcionario_id: funcionarioSelecionado,
          avaliadores_pares: avaliadoresPares,
          avaliadores_clientes: avaliadoresClientes,
          avaliadores_subordinados: avaliadoresSubordinados
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Avaliadores definidos com sucesso');

      // Limpar seleções
      setFuncionarioSelecionado('');
      setAvaliadoresPares([]);
      setAvaliadoresClientes([]);
      setAvaliadoresSubordinados([]);
    } catch (error) {
      console.error('Erro ao salvar avaliadores:', error);
      toast.error('Erro ao salvar avaliadores');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 flex items-center">
          <Users className="h-8 w-8 mr-3 text-purple-600" />
          Selecionar Avaliadores 360º
        </h1>
        <p className="text-gray-600 mt-1">
          Defina quem irá avaliar cada funcionário
        </p>
      </div>

      {/* Selecionar Funcionário a ser Avaliado */}
      <Card>
        <CardHeader>
          <CardTitle>1. Funcionário a ser Avaliado</CardTitle>
        </CardHeader>
        <CardContent>
          <select
            value={funcionarioSelecionado}
            onChange={(e) => setFuncionarioSelecionado(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2"
          >
            <option value="">Selecione um funcionário</option>
            {funcionarios.map(func => (
              <option key={func.id} value={func.id}>
                {func.nome}
              </option>
            ))}
          </select>
        </CardContent>
      </Card>

      {funcionarioSelecionado && (
        <>
          {carregandoFiltros ? (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
              <span className="ml-3 text-gray-600">Carregando avaliadores...</span>
            </div>
          ) : (
            <>
          {/* Pares */}
          <Card>
            <CardHeader>
              <CardTitle>2. Pares (mesmo nível hierárquico)</CardTitle>
              <p className="text-sm text-gray-600">Selecione 3-5 pares</p>
            </CardHeader>
            <CardContent>
              {paresDisponiveis.length === 0 ? (
                <p className="text-sm text-gray-500">Nenhum par encontrado para este colaborador</p>
              ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {paresDisponiveis
                  .slice(0, 10)
                  .map(func => (
                    <div
                      key={func.id}
                      className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                        avaliadoresPares.includes(func.id)
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-300 hover:border-blue-300'
                      }`}
                      onClick={() => toggleAvaliador('pares', func.id)}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-gray-900">{func.nome}</span>
                        {avaliadoresPares.includes(func.id) && (
                          <CheckCircle className="h-5 w-5 text-blue-600" />
                        )}
                      </div>
                    </div>
                  ))}
                  </div>
              )}
              <div className="mt-3">
                <Badge variant={avaliadoresPares.length >= 3 ? 'default' : 'secondary'}>
                  {avaliadoresPares.length} selecionados
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* Clientes Internos */}
          <Card>
            <CardHeader>
              <CardTitle>3. Clientes Internos (outras áreas)</CardTitle>
              <p className="text-sm text-gray-600">Selecione 2-3 clientes internos</p>
            </CardHeader>
            <CardContent>
              {clientesDisponiveis.length === 0 ? (
                <p className="text-sm text-gray-500">Nenhum cliente interno encontrado para este colaborador</p>
              ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {clientesDisponiveis
                  .slice(0, 8)
                  .map(func => (
                    <div
                      key={func.id}
                      className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                        avaliadoresClientes.includes(func.id)
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-gray-300 hover:border-purple-300'
                      }`}
                      onClick={() => toggleAvaliador('clientes', func.id)}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-gray-900">{func.nome}</span>
                        {avaliadoresClientes.includes(func.id) && (
                          <CheckCircle className="h-5 w-5 text-purple-600" />
                        )}
                      </div>
                    </div>
                  ))}
                  </div>
              )}
              <div className="mt-3">
                <Badge variant={avaliadoresClientes.length >= 2 ? 'default' : 'secondary'}>
                  {avaliadoresClientes.length} selecionados
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* Subordinados */}
          <Card>
            <CardHeader>
              <CardTitle>4. Subordinados (opcional)</CardTitle>
              <p className="text-sm text-gray-600">Selecione 2-4 subordinados se aplicável</p>
            </CardHeader>
            <CardContent>
              {subordinadosDisponiveis.length === 0 ? (
                <p className="text-sm text-gray-500">Nenhum subordinado encontrado para este colaborador</p>
              ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {subordinadosDisponiveis
                  .slice(0, 6)
                  .map(func => (
                    <div
                      key={func.id}
                      className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                        avaliadoresSubordinados.includes(func.id)
                          ? 'border-green-500 bg-green-50'
                          : 'border-gray-300 hover:border-green-300'
                      }`}
                      onClick={() => toggleAvaliador('subordinados', func.id)}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-gray-900">{func.nome}</span>
                        {avaliadoresSubordinados.includes(func.id) && (
                          <CheckCircle className="h-5 w-5 text-green-600" />
                        )}
                      </div>
                    </div>
                  ))}
                  </div>
              )}
              <div className="mt-3">
                <Badge variant="secondary">
                  {avaliadoresSubordinados.length} selecionados
                </Badge>
              </div>
            </CardContent>
          </Card>
          </>
          )}

          {/* Ações */}
          <div className="flex justify-end space-x-3">
            <Button
              variant="outline"
              onClick={() => navigate('/feedback/360')}
            >
              Cancelar
            </Button>
            <Button
              onClick={salvarAvaliadores}
              disabled={loading || avaliadoresPares.length < 3}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {loading ? 'Salvando...' : 'Salvar Avaliadores'}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default SelecionarAvaliadores360;